// src/pages/DriverVersionPage.js
import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig'; 
import { Save, Info, Smartphone, AlertTriangle } from 'lucide-react';

export default function DriverVersionPage() {
  const [latestVersion, setLatestVersion] = useState('');
  const [minVersion, setMinVersion] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchVersions();
  }, []);

  const fetchVersions = async () => {
    setLoading(true);
    try {
      // --- PERHATIKAN: Target dokumen kini diarahkan ke 'driver_version' ---
      const docRef = doc(db, 'app_settings', 'driver_version');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setLatestVersion(data.latest_version || '');
        setMinVersion(data.min_version || '');
      }
    } catch (error) {
      console.error('Error fetching versions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!latestVersion || !minVersion) {
      return alert('Mohon isi kedua kolom versi!');
    }

    setSaving(true);
    try {
      // --- PERHATIKAN: Target penyimpanan diarahkan ke 'driver_version' ---
      const docRef = doc(db, 'app_settings', 'driver_version');
      await setDoc(
        docRef,
        {
          latest_version: latestVersion,
          min_version: minVersion,
          updatedAt: new Date(),
        },
        { merge: true } 
      );
      alert('✅ Berhasil memperbarui versi aplikasi Zimo Driver!');
    } catch (error) {
      console.error('Error saving versions:', error);
      alert('❌ Gagal menyimpan data: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-gray-400">Memuat pengaturan versi...</div>;
  }

  return (
    <div className="p-6 text-gray-100 max-w-3xl">
      <h2 className="text-2xl font-bold mb-6 flex items-center gap-2 text-green-400">
        <Smartphone size={28} />
        Versi Aplikasi Zimo Driver
      </h2>

      {/* Info Panel */}
      <div className="bg-green-900/20 border border-green-800 p-4 rounded-xl mb-6">
        <h3 className="flex items-center gap-2 text-green-400 font-semibold mb-2">
          <Info size={18} /> Panduan Update
        </h3>
        <ul className="list-disc list-inside text-sm text-gray-300 space-y-2 ml-1">
          <li>
            <strong className="text-white">Versi Terbaru:</strong> Memunculkan notifikasi <i>opsional</i> (bisa dilewati) kepada driver bahwa ada versi aplikasi yang lebih baru.
          </li>
          <li>
            <strong className="text-white">Versi Minimal:</strong> Memunculkan notifikasi <i>wajib</i> (Force Update). Jika versi aplikasi driver berada di bawah versi ini, mereka <b>tidak bisa menggunakan aplikasi</b> sampai melakukan update di Play Store.
          </li>
        </ul>
      </div>

      {/* Form Input */}
      <form onSubmit={handleSave} className="bg-gray-900 border border-gray-800 p-6 rounded-xl shadow-lg">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          
          {/* Input Versi Terbaru */}
          <div>
            <label className="flex text-gray-300 font-semibold mb-2 items-center gap-2">
              Versi Terbaru (Latest Version)
            </label>
            <input
              type="text"
              placeholder="Contoh: 1.0.5"
              value={latestVersion}
              onChange={(e) => setLatestVersion(e.target.value)}
              className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-green-500"
            />
            <p className="text-xs text-gray-500 mt-2">
              Versi terkini yang rilis di Google Play Store.
            </p>
          </div>

          {/* Input Versi Minimal */}
          <div>
            <label className="flex text-red-400 font-semibold mb-2 items-center gap-2">
              <AlertTriangle size={16} />
              Versi Minimal (Force Update)
            </label>
            <input
              type="text"
              placeholder="Contoh: 1.0.3"
              value={minVersion}
              onChange={(e) => setMinVersion(e.target.value)}
              className="w-full p-3 bg-gray-800 border border-red-900/50 rounded-lg text-white focus:outline-none focus:border-red-500"
            />
            <p className="text-xs text-gray-500 mt-2">
              Batas versi paling bawah yang diizinkan beroperasi.
            </p>
          </div>
        </div>

        <button
          type="submit"
          disabled={saving}
          className="flex items-center justify-center gap-2 w-full md:w-auto px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
        >
          <Save size={18} />
          {saving ? 'Menyimpan...' : 'Simpan Pengaturan'}
        </button>
      </form>
    </div>
  );
}